/**
 * Debug wireframe overlay for all physics colliders.
 * Renders circle outlines for ball colliders and rectangles for cuboid colliders.
 * Color-coded by collision group membership.
 */

import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import { debugExportClientState } from '../systems/ClientPrediction';
import {
  GROUP_SHIP,
  GROUP_ASTEROID,
  GROUP_CELESTIAL,
  GROUP_BOUNDARY,
} from '@warmancer/shared';
import type { DebugColliderState } from '@warmancer/shared';

const CIRCLE_SEGMENTS = 32;
const WIREFRAME_Y = 0.1; // Slightly above ground plane
const UPDATE_INTERVAL = 2; // Update React state every 2 frames (~30Hz at 60fps)

function getColliderColor(collisionGroup: number): string {
  const membership = (collisionGroup >>> 16) & 0xFFFF;
  if (membership & GROUP_SHIP) return '#00ff00';
  if (membership & GROUP_CELESTIAL) return '#0088ff';
  if (membership & GROUP_ASTEROID) return '#ffff00';
  if (membership & GROUP_BOUNDARY) return '#ff0000';
  return '#ffffff';
}

function makeCirclePoints(radius: number): [number, number, number][] {
  const points: [number, number, number][] = [];
  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const angle = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    points.push([
      Math.cos(angle) * radius,
      WIREFRAME_Y,
      Math.sin(angle) * radius,
    ]);
  }
  return points;
}

function makeRectPoints(hx: number, hy: number): [number, number, number][] {
  return [
    [-hx, WIREFRAME_Y, -hy],
    [hx, WIREFRAME_Y, -hy],
    [hx, WIREFRAME_Y, hy],
    [-hx, WIREFRAME_Y, hy],
    [-hx, WIREFRAME_Y, -hy],
  ];
}

interface WireframeData {
  readonly key: string;
  readonly points: [number, number, number][];
  readonly color: string;
  readonly x: number;
  readonly z: number;
}

export function DebugWireframes() {
  const [wireframes, setWireframes] = useState<WireframeData[]>([]);
  const frameRef = useRef(0);

  useFrame(() => {
    frameRef.current++;
    if (frameRef.current % UPDATE_INTERVAL !== 0) return;

    const state = debugExportClientState();
    if (!state) {
      if (wireframes.length > 0) setWireframes([]);
      return;
    }

    const wires: WireframeData[] = [];
    for (let i = 0; i < state.colliders.length; i++) {
      const c: DebugColliderState = state.colliders[i];
      const color = getColliderColor(c.collisionGroup);

      if (c.shape === 'ball' && c.radius !== undefined) {
        wires.push({
          key: `c${i}`,
          points: makeCirclePoints(c.radius),
          color,
          x: c.x,
          z: c.y,
        });
      } else if (c.shape === 'cuboid' && c.halfExtentsX !== undefined && c.halfExtentsY !== undefined) {
        wires.push({
          key: `c${i}`,
          points: makeRectPoints(c.halfExtentsX, c.halfExtentsY),
          color,
          x: c.x,
          z: c.y,
        });
      }
    }
    setWireframes(wires);
  });

  return (
    <group>
      {wireframes.map((w) => (
        <group key={w.key} position={[w.x, 0, w.z]}>
          <Line
            points={w.points}
            color={w.color}
            lineWidth={1}
            transparent
            opacity={0.7}
          />
        </group>
      ))}
    </group>
  );
}
